import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  foodsApi,
  mediaApi,
  mealLogsApi,
  setsApi,
  weightLogsApi,
  type DayLogs,
  type Food,
  type MealSet,
  type User,
} from "../api/client";
import { AiKcalButton } from "../components/AiKcalButton";
import { MealLogEntry } from "../components/MealLogEntry";
import { PageHeader } from "../components/PageHeader";
import {
  dateTimeLocalToIso,
  nowDateTimeLocalValue,
  nowLocalIso,
  nowTimeLocalValue,
  timeOnDateToIso,
  todayLocalDate,
} from "../lib/date";
import { resizeImageToJpegBlob } from "../lib/media";
import cameraIcon from "../../../camera.svg";
import fileUploadIcon from "../../../file-upload.svg";
import weightIcon from "../../../monitor_weight_24dp_1F1F1F_FILL0_wght400_GRAD0_opsz24.svg";

const desktopTimeHours = Array.from({ length: 24 }, (_, index) =>
  String(index).padStart(2, "0"),
);
const desktopTimeMinutes = Array.from({ length: 60 }, (_, index) =>
  String(index).padStart(2, "0"),
);

const existingFoodPortionOptions = [0.25, 0.5, 0.75, 1, 1.5, 2, 2.5] as const;

function formatExistingFoodPortionLabel(multiplier: number) {
  return `${Math.round(multiplier * 100)}%`;
}

export type DailyMealLogsPageMode = "today" | "history";

function splitTimeValue(value: string) {
  const [hour = "00", minute = "00"] = value.split(":");
  return { hour, minute };
}

function shouldUseNativeCameraCapture() {
  if (typeof navigator === "undefined") return false;

  const userAgent = navigator.userAgent;
  const isIOSDevice = /iPhone|iPad|iPod/i.test(userAgent);
  const isTouchMac =
    /Macintosh/i.test(userAgent) && navigator.maxTouchPoints > 1;
  return isIOSDevice || isTouchMac;
}

function isFinePointer() {
  if (typeof window === "undefined") return true;
  return window.matchMedia("(pointer: fine)").matches;
}

function defaultWeightDateTime(mode: DailyMealLogsPageMode, date: string) {
  return mode === "today" ? nowDateTimeLocalValue() : `${date}T07:00`;
}

function DesktopTimePicker({
  buttonId,
  value,
  onChange,
}: {
  buttonId: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const { hour, minute } = splitTimeValue(value);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const updateHour = (nextHour: string) => {
    onChange(`${nextHour}:${minute}`);
  };

  const updateMinute = (nextMinute: string) => {
    onChange(`${hour}:${nextMinute}`);
  };

  return (
    <div
      ref={rootRef}
      className={`desktop-time-picker${open ? " is-open" : ""}`}
    >
      <button
        id={buttonId}
        type="button"
        className="quick-create-time-display desktop-time-trigger"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <span className="desktop-time-trigger-label">摂取時刻</span>
        <span className="desktop-time-trigger-value">{value || "未設定"}</span>
      </button>

      {open && (
        <div
          className="desktop-time-panel"
          role="dialog"
          aria-label="摂取時刻を選択"
        >
          <div className="desktop-time-panel-header">
            <div className="desktop-time-panel-value">{value || "未設定"}</div>
            <button
              type="button"
              className="desktop-time-now-button"
              onClick={() => onChange(nowTimeLocalValue())}
            >
              現在
            </button>
          </div>

          <div className="desktop-time-columns">
            <div className="desktop-time-column">
              <div className="desktop-time-column-label">Hour</div>
              <div
                className="desktop-time-option-list"
                role="listbox"
                aria-label="時"
              >
                {desktopTimeHours.map((option) => (
                  <button
                    key={option}
                    type="button"
                    className={`desktop-time-option${option === hour ? " is-active" : ""}`}
                    onClick={() => updateHour(option)}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>

            <div className="desktop-time-column">
              <div className="desktop-time-column-label">Minute</div>
              <div
                className="desktop-time-option-list"
                role="listbox"
                aria-label="分"
              >
                {desktopTimeMinutes.map((option) => (
                  <button
                    key={option}
                    type="button"
                    className={`desktop-time-option${option === minute ? " is-active" : ""}`}
                    onClick={() => updateMinute(option)}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="desktop-time-panel-actions">
            <button
              type="button"
              className="btn-secondary desktop-time-close-button"
              onClick={() => setOpen(false)}
            >
              閉じる
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function DailyMealLogsPage({
  user,
  date,
  mode,
}: {
  user: User;
  date: string;
  mode: DailyMealLogsPageMode;
}) {
  const navigate = useNavigate();
  const [foods, setFoods] = useState<Food[]>([]);
  const [sets, setSets] = useState<MealSet[]>([]);
  const [day, setDay] = useState<DayLogs | null>(null);
  const [search, setSearch] = useState("");
  const [selectedFoodId, setSelectedFoodId] = useState<string | null>(null);
  const [draftKcal, setDraftKcal] = useState("");
  const [existingFoodPortion, setExistingFoodPortion] = useState("1");
  const [draftEatenTime, setDraftEatenTime] = useState(nowTimeLocalValue());
  const [savingDraft, setSavingDraft] = useState(false);
  const [pendingPhotoFile, setPendingPhotoFile] = useState<File | null>(null);
  const [pendingPhotoUrl, setPendingPhotoUrl] = useState<string | null>(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [capturingPhoto, setCapturingPhoto] = useState(false);
  const [weightOpen, setWeightOpen] = useState(false);
  const [weightKg, setWeightKg] = useState("");
  const [weightRecordedAt, setWeightRecordedAt] = useState(
    defaultWeightDateTime(mode, date),
  );
  const [savingWeight, setSavingWeight] = useState(false);
  const [quickFoodId, setQuickFoodId] = useState("");
  const [quickSetId, setQuickSetId] = useState("");
  const [savingQuickPick, setSavingQuickPick] = useState(false);
  const [mediaMenuOpen, setMediaMenuOpen] = useState(false);
  const cameraInputRef = useRef<HTMLInputElement | null>(null);
  const uploadInputRef = useRef<HTMLInputElement | null>(null);
  const draftTimeInputRef = useRef<HTMLInputElement | null>(null);
  const weightDateTimeInputRef = useRef<HTMLInputElement | null>(null);
  const cameraVideoRef = useRef<HTMLVideoElement | null>(null);
  const cameraStreamRef = useRef<MediaStream | null>(null);
  const mediaMenuRef = useRef<HTMLDivElement | null>(null);

  const isTodayMode = mode === "today";

  const reloadAll = async () => {
    const [f, s, d] = await Promise.all([
      foodsApi.list(),
      setsApi.list(),
      mealLogsApi.byDate(date),
    ]);
    setFoods(f);
    setSets(s);
    setDay(d);
  };

  useEffect(() => {
    reloadAll().catch(console.error);
  }, [date]);

  useEffect(() => {
    setWeightRecordedAt(defaultWeightDateTime(mode, date));
    setWeightOpen(false);
  }, [date, mode]);

  useEffect(() => {
    if (!pendingPhotoFile) {
      setPendingPhotoUrl(null);
      return;
    }

    const nextUrl = URL.createObjectURL(pendingPhotoFile);
    setPendingPhotoUrl(nextUrl);
    return () => URL.revokeObjectURL(nextUrl);
  }, [pendingPhotoFile]);

  useEffect(() => {
    return () => {
      stopCameraStream(cameraStreamRef.current);
    };
  }, []);

  useEffect(() => {
    if (!mediaMenuOpen) return;

    const onPointerDown = (event: MouseEvent) => {
      if (!mediaMenuRef.current?.contains(event.target as Node)) {
        setMediaMenuOpen(false);
      }
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMediaMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [mediaMenuOpen]);

  useEffect(() => {
    if (
      draftTimeInputRef.current &&
      draftTimeInputRef.current.value !== draftEatenTime
    ) {
      draftTimeInputRef.current.value = draftEatenTime;
    }
  }, [draftEatenTime]);

  useEffect(() => {
    if (
      weightDateTimeInputRef.current &&
      weightDateTimeInputRef.current.value !== weightRecordedAt
    ) {
      weightDateTimeInputRef.current.value = weightRecordedAt;
    }
  }, [weightRecordedAt]);

  useEffect(() => {
    const q = search.trim();
    if (q.length === 0) {
      foodsApi.list().then(setFoods).catch(console.error);
      return;
    }
    const timer = setTimeout(() => {
      foodsApi.list(q).then(setFoods).catch(console.error);
    }, 200);
    return () => clearTimeout(timer);
  }, [search]);

  const totalKcal = day?.total_kcal ?? 0;
  const targetKcal =
    day?.target_kcal ?? (isTodayMode ? user.daily_target_kcal : 0);
  const remaining = targetKcal - totalKcal;
  const minWeightKg = day?.min_weight_kg ?? null;
  const trimmedSearch = search.trim();
  const exactFoodMatch = foods.find(
    (food) =>
      food.name.trim().toLocaleLowerCase() ===
      trimmedSearch.toLocaleLowerCase(),
  );
  const selectedFood = foods.find((food) => food.id === selectedFoodId) ?? null;
  const activeFood = selectedFood ?? exactFoodMatch ?? null;
  const existingFoodPortionMultiplier = Number(existingFoodPortion);
  const adjustedActiveFoodKcal = activeFood
    ? Math.round(activeFood.kcal * existingFoodPortionMultiplier)
    : 0;
  const hasSearchResults = trimmedSearch.length > 0 && foods.length > 0;
  const showComposer = pendingPhotoUrl !== null || trimmedSearch.length > 0;
  const showCompactComposer =
    pendingPhotoUrl === null &&
    trimmedSearch.length > 0 &&
    (Boolean(activeFood) || !hasSearchResults);
  const canCreateFromSearch =
    trimmedSearch.length > 0 &&
    !hasSearchResults &&
    Number(draftKcal) > 0 &&
    draftEatenTime.length > 0;
  const canRecordExisting = Boolean(activeFood) && draftEatenTime.length > 0;
  const finePointer = isFinePointer();

  useEffect(() => {
    if (trimmedSearch.length === 0) {
      setSelectedFoodId(null);
      return;
    }

    if (selectedFoodId && !foods.some((food) => food.id === selectedFoodId)) {
      setSelectedFoodId(null);
      return;
    }

    if (!selectedFoodId && exactFoodMatch) {
      setSelectedFoodId(exactFoodMatch.id);
    }
  }, [exactFoodMatch, foods, selectedFoodId, trimmedSearch]);

  const resetComposer = () => {
    setSearch("");
    setSelectedFoodId(null);
    setDraftKcal("");
    setExistingFoodPortion("1");
    setDraftEatenTime(nowTimeLocalValue());
    setPendingPhotoFile(null);
  };

  const syncDraftEatenTime = (value: string) => {
    setDraftEatenTime(value);
  };

  const syncWeightRecordedAt = (value: string) => {
    setWeightRecordedAt(value);
  };

  const onDraftTimeChange = (value: string) => {
    if (isFinePointer()) {
      syncDraftEatenTime(value);
    }
  };

  const onWeightRecordedAtChange = (value: string) => {
    if (isFinePointer()) {
      syncWeightRecordedAt(value);
    }
  };

  const openPickerForFinePointer = (input: HTMLInputElement | null) => {
    if (!input) return;
    if (
      typeof window !== "undefined" &&
      !window.matchMedia("(pointer: fine)").matches
    ) {
      return;
    }
    if (typeof input.showPicker === "function") {
      input.showPicker();
      return;
    }
    input.focus();
    input.click();
  };

  const immediateEatenAt = isTodayMode
    ? nowLocalIso()
    : timeOnDateToIso(date, draftEatenTime);

  const onCopyToToday = async () => {
    await mealLogsApi.copyFromDate(date, nowLocalIso());
    navigate("/");
  };

  const onTapFood = async (food: Food) => {
    await createLog({
      eaten_at: immediateEatenAt,
      food_name: food.name,
      kcal: food.kcal,
      food_id: food.id,
    });
    await reloadAll();
  };

  const onTapSet = async (set: MealSet) => {
    await mealLogsApi.fromSet(set.id, immediateEatenAt);
    await reloadAll();
  };

  const onSubmitQuickFood = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!quickFoodId) return;
    const food = foods.find((item) => item.id === quickFoodId);
    if (!food) return;

    setSavingQuickPick(true);
    try {
      await onTapFood(food);
      setQuickFoodId("");
    } finally {
      setSavingQuickPick(false);
    }
  };

  const onSubmitQuickSet = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!quickSetId) return;
    const mealSet = sets.find((item) => item.id === quickSetId);
    if (!mealSet) return;

    setSavingQuickPick(true);
    try {
      await onTapSet(mealSet);
      setQuickSetId("");
    } finally {
      setSavingQuickPick(false);
    }
  };

  const onDeleteLog = async (id: string) => {
    await mealLogsApi.remove(id);
    await reloadAll();
  };

  const onCreateFoodFromSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canCreateFromSearch) return;
    setSavingDraft(true);
    try {
      const createdFood = await foodsApi.create({
        name: trimmedSearch,
        kcal: Number(draftKcal),
      });
      await createLog({
        eaten_at: timeOnDateToIso(date, draftEatenTime),
        food_name: createdFood.name,
        kcal: createdFood.kcal,
        food_id: createdFood.id,
      });
      resetComposer();
      await reloadAll();
    } finally {
      setSavingDraft(false);
    }
  };

  const onRecordExistingFromSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeFood || !canRecordExisting) return;
    setSavingDraft(true);
    try {
      await createLog({
        eaten_at: timeOnDateToIso(date, draftEatenTime),
        food_name: activeFood.name,
        kcal: adjustedActiveFoodKcal,
        food_id: activeFood.id,
      });
      resetComposer();
      await reloadAll();
    } finally {
      setSavingDraft(false);
    }
  };

  const onPickPendingPhoto = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    event.target.value = "";
    if (!file) return;
    setPendingPhotoFile(file);
  };

  const clearPendingPhoto = () => {
    setPendingPhotoFile(null);
  };

  const openCamera = async () => {
    if (
      shouldUseNativeCameraCapture() ||
      !navigator.mediaDevices?.getUserMedia
    ) {
      cameraInputRef.current?.click();
      return;
    }

    setCameraError(null);
    try {
      stopCameraStream(cameraStreamRef.current);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: "environment" },
        },
        audio: false,
      });
      cameraStreamRef.current = stream;
      setCameraOpen(true);

      requestAnimationFrame(() => {
        if (!cameraVideoRef.current) return;
        cameraVideoRef.current.srcObject = stream;
        cameraVideoRef.current.play().catch(() => {
          closeCamera();
          cameraInputRef.current?.click();
        });
      });
    } catch (error) {
      console.error(error);
      cameraInputRef.current?.click();
    }
  };

  const closeCamera = () => {
    setCameraOpen(false);
    stopCameraStream(cameraStreamRef.current);
    cameraStreamRef.current = null;
    if (cameraVideoRef.current) {
      cameraVideoRef.current.srcObject = null;
    }
  };

  const captureFromCamera = async () => {
    if (!cameraVideoRef.current) return;
    setCapturingPhoto(true);
    try {
      const blob = await captureVideoFrame(cameraVideoRef.current);
      const file = new File([blob], `camera-${Date.now()}.jpg`, {
        type: blob.type,
      });
      setPendingPhotoFile(file);
      closeCamera();
    } catch (error) {
      console.error(error);
      setCameraError("写真の取得に失敗しました。");
    } finally {
      setCapturingPhoto(false);
    }
  };

  return (
    <div className="page">
      {!isTodayMode && (
        <PageHeader
          title={date}
          backTo="/history"
          right={
            date !== todayLocalDate() ? (
              <button className="btn-pearl" onClick={onCopyToToday}>
                今日にコピー
              </button>
            ) : null
          }
        />
      )}

      <div className="summary-card">
        <div className="summary-target">
          {isTodayMode ? "本日の摂取カロリー" : "合計"}
        </div>
        <div className="summary-kcal">{totalKcal} kcal</div>
        <div className="summary-target" style={{ marginTop: 6 }}>
          目標 {targetKcal} kcal / 残り {remaining} kcal
        </div>
        {!isTodayMode && (
          <div className="summary-target" style={{ marginTop: 6 }}>
            体重 {minWeightKg !== null ? `${minWeightKg} kg` : "未記録"}
          </div>
        )}
        <button
          type="button"
          className="summary-weight-button"
          onClick={() => setWeightOpen(true)}
        >
          <img
            src={weightIcon}
            alt=""
            aria-hidden="true"
            className="summary-weight-button-icon"
          />
          <span>体重登録</span>
        </button>
      </div>

      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        onChange={onPickPendingPhoto}
      />
      <input
        ref={uploadInputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={onPickPendingPhoto}
      />

      <div
        className={`quick-create-card${showCompactComposer ? " quick-create-card-compact" : ""}`}
      >
        <div className="search-input-row quick-create-search-row">
          <input
            className="quick-create-search-input"
            type="text"
            lang="ja"
            inputMode="text"
            placeholder="食品名"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setSelectedFoodId(null);
            }}
          />
          <AiKcalButton foodName={trimmedSearch} buttonLabel="AI㌍" compact />
          <div ref={mediaMenuRef} className="quick-create-menu">
            <button
              type="button"
              className="quick-create-plus-button"
              aria-label="写真メニューを開く"
              aria-haspopup="menu"
              aria-expanded={mediaMenuOpen}
              onClick={() => setMediaMenuOpen((current) => !current)}
            >
              ＋
            </button>

            {mediaMenuOpen && (
              <div
                className="quick-create-menu-panel"
                role="menu"
                aria-label="写真メニュー"
              >
                <button
                  type="button"
                  className="quick-create-menu-item"
                  role="menuitem"
                  onClick={() => {
                    setMediaMenuOpen(false);
                    void openCamera();
                  }}
                >
                  <img
                    src={cameraIcon}
                    alt=""
                    aria-hidden="true"
                    className="quick-create-menu-icon"
                  />
                  <span>カメラを起動</span>
                </button>
                <button
                  type="button"
                  className="quick-create-menu-item"
                  role="menuitem"
                  onClick={() => {
                    setMediaMenuOpen(false);
                    uploadInputRef.current?.click();
                  }}
                >
                  <img
                    src={fileUploadIcon}
                    alt=""
                    aria-hidden="true"
                    className="quick-create-menu-icon"
                  />
                  <span>写真をアップ</span>
                </button>
              </div>
            )}
          </div>
        </div>

        {trimmedSearch.length > 0 && foods.length > 0 && !activeFood && (
          <div
            className="search-suggestion-list quick-create-suggestion-list"
            role="listbox"
            aria-label="食品候補"
          >
            {foods.slice(0, 6).map((food) => (
              <button
                key={food.id}
                type="button"
                className={`search-suggestion-item${selectedFoodId === food.id ? " is-selected" : ""}`}
                onClick={() => {
                  setSearch(food.name);
                  setSelectedFoodId(food.id);
                }}
              >
                <span className="search-suggestion-name">{food.name}</span>
                <span className="search-suggestion-kcal">{food.kcal} kcal</span>
              </button>
            ))}
          </div>
        )}

        {showComposer && (
          <>
            {!showCompactComposer && (
              <div className="quick-create-title">
                {trimmedSearch.length === 0
                  ? "食品名を入力すると、そのまま記録できます"
                  : activeFood
                    ? "選択した食品を記録できます"
                    : hasSearchResults
                      ? "候補から食品を選択してください"
                      : "検索結果にないので新規登録できます"}
              </div>
            )}

            {!showCompactComposer && trimmedSearch.length > 0 && (
              <div className="quick-create-name">{trimmedSearch}</div>
            )}

            {pendingPhotoUrl && (
              <div className="pending-photo-card" style={{ marginBottom: 0 }}>
                <img
                  src={pendingPhotoUrl}
                  alt="追加予定の写真"
                  className="pending-photo-preview"
                />
                <div className="pending-photo-actions">
                  <span className="muted">
                    記録時にこの写真も一緒に添付します。
                  </span>
                  <button
                    type="button"
                    className="btn-link"
                    onClick={clearPendingPhoto}
                  >
                    写真を外す
                  </button>
                </div>
              </div>
            )}

            {!showCompactComposer && (
              <div
                className={`quick-create-grid${!activeFood && !hasSearchResults ? " quick-create-grid-dual" : ""}`}
              >
                {!activeFood && !hasSearchResults && (
                  <div
                    className="field quick-create-kcal-field"
                    style={{ marginBottom: 0 }}
                  >
                    <label htmlFor={`${mode}-create-kcal`}>
                      カロリー (kcal)
                    </label>
                    <input
                      id={`${mode}-create-kcal`}
                      inputMode="numeric"
                      value={draftKcal}
                      onChange={(e) => setDraftKcal(e.target.value)}
                      placeholder="例: 320"
                    />
                  </div>
                )}
                <div
                  className="field quick-create-time-field"
                  style={{ marginBottom: 0 }}
                >
                  <label
                    htmlFor={`${mode}-time`}
                    className={
                      finePointer ? "quick-create-time-label-spacer" : undefined
                    }
                  >
                    摂取時刻
                  </label>
                  {finePointer ? (
                    <DesktopTimePicker
                      buttonId={`${mode}-time-desktop`}
                      value={draftEatenTime}
                      onChange={syncDraftEatenTime}
                    />
                  ) : (
                    <div className="quick-create-time-control">
                      <button
                        type="button"
                        className="quick-create-time-display"
                        onClick={() =>
                          openPickerForFinePointer(draftTimeInputRef.current)
                        }
                      >
                        {draftEatenTime || "未設定"}
                      </button>
                      <input
                        ref={draftTimeInputRef}
                        id={`${mode}-time`}
                        className="quick-create-time-native"
                        type="time"
                        defaultValue={draftEatenTime}
                        aria-label="摂取時刻"
                        onChange={(e) =>
                          onDraftTimeChange(e.currentTarget.value)
                        }
                        onBlur={(e) =>
                          syncDraftEatenTime(e.currentTarget.value)
                        }
                      />
                    </div>
                  )}
                </div>
              </div>
            )}

            {showCompactComposer ? (
              activeFood ? (
                <form
                  className="quick-create-compact-form"
                  onSubmit={onRecordExistingFromSearch}
                >
                  <div className="quick-create-compact-header">
                    <div className="quick-create-name quick-create-name-compact">
                      {activeFood.name}
                    </div>
                    <div className="quick-create-existing-kcal quick-create-existing-kcal-compact">
                      {adjustedActiveFoodKcal} kcal
                    </div>
                  </div>

                  <div className="quick-create-compact-actions">
                    <div
                      className="field quick-create-time-field quick-create-time-field-compact"
                      style={{ marginBottom: 0 }}
                    >
                      {!finePointer && (
                        <label htmlFor={`${mode}-time`}>摂取時刻</label>
                      )}
                      {finePointer ? (
                        <DesktopTimePicker
                          buttonId={`${mode}-time-desktop`}
                          value={draftEatenTime}
                          onChange={syncDraftEatenTime}
                        />
                      ) : (
                        <div className="quick-create-time-control">
                          <button
                            type="button"
                            className="quick-create-time-display"
                            onClick={() =>
                              openPickerForFinePointer(
                                draftTimeInputRef.current,
                              )
                            }
                          >
                            {draftEatenTime || "未設定"}
                          </button>
                          <input
                            ref={draftTimeInputRef}
                            id={`${mode}-time`}
                            className="quick-create-time-native"
                            type="time"
                            defaultValue={draftEatenTime}
                            aria-label="摂取時刻"
                            onChange={(e) =>
                              onDraftTimeChange(e.currentTarget.value)
                            }
                            onBlur={(e) =>
                              syncDraftEatenTime(e.currentTarget.value)
                            }
                          />
                        </div>
                      )}
                    </div>

                    <div
                      className="field quick-create-portion-field quick-create-portion-field-compact"
                      style={{ marginBottom: 0 }}
                    >
                      {!finePointer && (
                        <label htmlFor={`${mode}-portion`}>摂取量</label>
                      )}
                      <div className="quick-create-portion-note">食べた量</div>
                      <select
                        id={`${mode}-portion`}
                        value={existingFoodPortion}
                        aria-label="摂取量"
                        onChange={(e) =>
                          setExistingFoodPortion(e.currentTarget.value)
                        }
                      >
                        {existingFoodPortionOptions.map((option) => (
                          <option key={option} value={String(option)}>
                            {formatExistingFoodPortionLabel(option)}
                          </option>
                        ))}
                      </select>
                    </div>

                    <button
                      type="submit"
                      className="btn-primary quick-create-submit-compact"
                      disabled={!canRecordExisting || savingDraft}
                    >
                      {savingDraft ? "記録中…" : "この食品を記録"}
                    </button>
                  </div>
                </form>
              ) : (
                <form
                  className="quick-create-compact-form"
                  onSubmit={onCreateFoodFromSearch}
                >
                  <div className="quick-create-compact-header">
                    <div className="quick-create-name quick-create-name-compact">
                      {trimmedSearch}
                    </div>
                    <div
                      className="field quick-create-kcal-field quick-create-kcal-field-compact"
                      style={{ marginBottom: 0 }}
                    >
                      <input
                        id={`${mode}-create-kcal-compact`}
                        inputMode="numeric"
                        value={draftKcal}
                        onChange={(e) => setDraftKcal(e.target.value)}
                        placeholder="0 Kcal"
                        aria-label="カロリー"
                      />
                    </div>
                  </div>

                  <div className="quick-create-compact-actions">
                    <div
                      className="field quick-create-time-field quick-create-time-field-compact"
                      style={{ marginBottom: 0 }}
                    >
                      {!finePointer && (
                        <label htmlFor={`${mode}-time`}>摂取時刻</label>
                      )}
                      {finePointer ? (
                        <DesktopTimePicker
                          buttonId={`${mode}-time-desktop`}
                          value={draftEatenTime}
                          onChange={syncDraftEatenTime}
                        />
                      ) : (
                        <div className="quick-create-time-control">
                          <button
                            type="button"
                            className="quick-create-time-display"
                            onClick={() =>
                              openPickerForFinePointer(
                                draftTimeInputRef.current,
                              )
                            }
                          >
                            {draftEatenTime || "未設定"}
                          </button>
                          <input
                            ref={draftTimeInputRef}
                            id={`${mode}-time`}
                            className="quick-create-time-native"
                            type="time"
                            defaultValue={draftEatenTime}
                            aria-label="摂取時刻"
                            onChange={(e) =>
                              onDraftTimeChange(e.currentTarget.value)
                            }
                            onBlur={(e) =>
                              syncDraftEatenTime(e.currentTarget.value)
                            }
                          />
                        </div>
                      )}
                    </div>

                    <button
                      type="submit"
                      className="btn-primary quick-create-submit-compact"
                      disabled={!canCreateFromSearch || savingDraft}
                    >
                      {savingDraft ? "作成中…" : "新規作成して登録"}
                    </button>
                  </div>
                </form>
              )
            ) : activeFood ? (
              <form onSubmit={onRecordExistingFromSearch}>
                <div className="quick-create-existing-kcal">
                  {adjustedActiveFoodKcal} kcal
                </div>
                <div className="field quick-create-portion-field">
                  <label htmlFor={`${mode}-portion-inline`}>摂取量</label>
                  <select
                    id={`${mode}-portion-inline`}
                    value={existingFoodPortion}
                    aria-label="摂取量"
                    onChange={(e) =>
                      setExistingFoodPortion(e.currentTarget.value)
                    }
                  >
                    {existingFoodPortionOptions.map((option) => (
                      <option key={option} value={String(option)}>
                        {formatExistingFoodPortionLabel(option)}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={!canRecordExisting || savingDraft}
                >
                  {savingDraft ? "記録中…" : "この食品を記録"}
                </button>
              </form>
            ) : !hasSearchResults && trimmedSearch.length > 0 ? (
              <form onSubmit={onCreateFoodFromSearch}>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={!canCreateFromSearch || savingDraft}
                >
                  {savingDraft ? "作成中…" : "新規作成して記録♪"}
                </button>
              </form>
            ) : null}
          </>
        )}
      </div>

      {foods.length > 0 ? (
        <form className="quick-pick-form" onSubmit={onSubmitQuickFood}>
          <div className="quick-pick-row">
            <select
              className="quick-pick-select"
              value={quickFoodId}
              aria-label="よく食べる食品を選択"
              onChange={(e) => setQuickFoodId(e.currentTarget.value)}
              disabled={savingQuickPick}
            >
              <option value="">よく食べる食品を選択</option>
              {foods.map((food) => (
                <option key={food.id} value={food.id}>
                  {food.name} ({food.kcal} kcal)
                </option>
              ))}
            </select>
            <button
              type="submit"
              className="btn-primary quick-pick-button"
              disabled={!quickFoodId || savingQuickPick}
            >
              {savingQuickPick ? "記録中…" : "記録"}
            </button>
          </div>
        </form>
      ) : (
        <p className="muted">
          {trimmedSearch.length === 0
            ? "まだ食品が登録されていません。"
            : "一致する食品が見つかりません。"}
        </p>
      )}

      {sets.length > 0 && (
        <form className="quick-pick-form" onSubmit={onSubmitQuickSet}>
          <div className="quick-pick-row">
            <select
              className="quick-pick-select"
              value={quickSetId}
              aria-label="セットを選択"
              onChange={(e) => setQuickSetId(e.currentTarget.value)}
              disabled={savingQuickPick}
            >
              <option value="">セットを選択</option>
              {sets.map((mealSet) => (
                <option key={mealSet.id} value={mealSet.id}>
                  {mealSet.name} ({mealSet.total_kcal} kcal)
                </option>
              ))}
            </select>
            <button
              type="submit"
              className="btn-primary quick-pick-button"
              disabled={!quickSetId || savingQuickPick}
            >
              {savingQuickPick ? "記録中…" : "記録"}
            </button>
          </div>
        </form>
      )}

      <h2 className="section-title">
        {isTodayMode ? "今日記録したもの" : "この日の記録"}
      </h2>
      <div className="today-logs-card">
        {day && day.logs.length > 0 ? (
          day.logs.map((log) => (
            <MealLogEntry
              key={log.id}
              log={log}
              onChanged={reloadAll}
              onDelete={onDeleteLog}
            />
          ))
        ) : (
          <p className="muted">
            {isTodayMode ? "まだ記録がありません。" : "記録がありません。"}
          </p>
        )}
      </div>

      {cameraOpen && (
        <div className="modal-backdrop" role="presentation">
          <div
            className="modal-card camera-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby={`${mode}-camera-modal-title`}
          >
            <h2 id={`${mode}-camera-modal-title`} className="modal-title">
              カメラ
            </h2>
            <video
              ref={cameraVideoRef}
              className="camera-preview"
              autoPlay
              playsInline
              muted
            />
            {cameraError && (
              <p className="meal-log-media-error">{cameraError}</p>
            )}
            <div className="camera-modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={closeCamera}
              >
                閉じる
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={captureFromCamera}
                disabled={capturingPhoto}
              >
                {capturingPhoto ? "撮影中…" : "撮影する"}
              </button>
            </div>
          </div>
        </div>
      )}

      {weightOpen && (
        <div className="modal-backdrop" role="presentation">
          <div
            className="modal-card"
            role="dialog"
            aria-modal="true"
            aria-labelledby={`${mode}-weight-modal-title`}
          >
            <h2 id={`${mode}-weight-modal-title`} className="modal-title">
              体重を登録
            </h2>
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const num = Number(weightKg);
                if (!num || !weightRecordedAt) return;
                setSavingWeight(true);
                try {
                  await weightLogsApi.create({
                    recorded_at: dateTimeLocalToIso(weightRecordedAt),
                    weight_kg: num,
                  });
                  setWeightKg("");
                  setWeightRecordedAt(defaultWeightDateTime(mode, date));
                  setWeightOpen(false);
                  await reloadAll();
                } finally {
                  setSavingWeight(false);
                }
              }}
            >
              <div className="field">
                <label htmlFor={`${mode}-weight-kg`}>体重 (kg)</label>
                <input
                  id={`${mode}-weight-kg`}
                  inputMode="decimal"
                  value={weightKg}
                  onChange={(e) => setWeightKg(e.target.value)}
                />
              </div>
              <div className="field">
                <label htmlFor={`${mode}-weight-recorded-at`}>計測日時</label>
                <div className="quick-create-time-control">
                  <button
                    type="button"
                    className="quick-create-time-display"
                    onClick={() =>
                      openPickerForFinePointer(weightDateTimeInputRef.current)
                    }
                  >
                    {weightRecordedAt
                      ? weightRecordedAt.replace("T", " ").replaceAll("-", "/")
                      : "未設定"}
                  </button>
                  <input
                    ref={weightDateTimeInputRef}
                    id={`${mode}-weight-recorded-at`}
                    className="quick-create-time-native"
                    type="datetime-local"
                    defaultValue={weightRecordedAt}
                    aria-label="計測日時"
                    onChange={(e) =>
                      onWeightRecordedAtChange(e.currentTarget.value)
                    }
                    onBlur={(e) => syncWeightRecordedAt(e.currentTarget.value)}
                  />
                </div>
              </div>
              <div className="camera-modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    setWeightOpen(false);
                    setWeightKg("");
                    setWeightRecordedAt(defaultWeightDateTime(mode, date));
                  }}
                >
                  閉じる
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={savingWeight || !weightKg || !weightRecordedAt}
                >
                  {savingWeight ? "保存中…" : "保存する"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );

  async function createLog(body: {
    eaten_at: string;
    food_name: string;
    kcal: number;
    food_id?: string;
  }) {
    const createdLog = await mealLogsApi.create(body);
    if (pendingPhotoFile) {
      await uploadPendingPhoto(createdLog.id, pendingPhotoFile);
    }
    return createdLog;
  }
}

async function uploadPendingPhoto(mealLogId: string, file: File) {
  const blob = await resizeImageToJpegBlob(file);
  const upload = await mediaApi.createUploadUrl({
    meal_log_id: mealLogId,
    content_type: blob.type,
  });
  await mediaApi.upload(upload.media_id, blob);
  await mediaApi.confirm(upload.media_id);
}

function stopCameraStream(stream: MediaStream | null) {
  stream?.getTracks().forEach((track) => track.stop());
}

function captureVideoFrame(video: HTMLVideoElement): Promise<Blob> {
  const width = video.videoWidth;
  const height = video.videoHeight;
  if (!width || !height) {
    throw new Error("Camera stream is not ready");
  }

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Canvas is not available");
  }

  context.drawImage(video, 0, 0, width, height);

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Failed to capture camera frame"));
          return;
        }
        resolve(blob);
      },
      "image/jpeg",
      0.92,
    );
  });
}
